/**
 * MatchingForm
 * Type-specific prompt form for the Matching AI content type
 * (QT_MATCHING_BASE). Renders media selection, num_left / num_right, and a
 * shared PromptDescriptionInput with config-driven source-type buttons.
 * Quick Create dialog is managed by PromptFormRenderer; this form
 * auto-applies template defaults when questionType changes.
 */

import './MatchingForm.css';

import * as React from 'react';
import find from 'lodash/find';
import {
    cn,
    Button,
    Input,
    Label,
    RadioGroup,
    RadioGroupItem,
    Card,
    CardContent,
} from '@ai-content-hub/content-kit';
import { mediaOptionLabel, usePromptTranslations, getPromptLocale } from '../i18n';
import { getFormTypeDisplayName } from '../questionTypeLabels';
import { Sparkles, Loader2, RefreshCw } from 'lucide-react';
import type { PromptFormProps } from '../types';
import type { UploadedDocument } from '../types';
import { buildDocumentSubmitFields } from '../documentSubmit';
import { PromptDescriptionInput, JobDetailsBar } from '../components';
import {
    MEDIA_OPTIONS,
    DEFAULT_FORM_STATE,
    MIN_NUM_LEFT,
    MAX_NUM_LEFT,
    MIN_NUM_RIGHT,
    MAX_NUM_RIGHT,
    type MatchingMediaType,
    type MatchingFormState,
} from './types';
import { QUESTION_TYPE_PRESETS, QUICK_CREATE_TEMPLATES } from './config';

const DESCRIPTION_MAX_LENGTH = 500;

function clampNumLeft(raw: number): number {
    return Math.min(MAX_NUM_LEFT, Math.max(MIN_NUM_LEFT, raw));
}

function clampNumRight(raw: number, numLeft: number): number {
    return Math.min(MAX_NUM_RIGHT, Math.max(Math.max(MIN_NUM_RIGHT, numLeft), raw));
}

export const MatchingForm: React.FC<PromptFormProps> = ({
    questionType,
    questionTypeOptions,
    allQuestionTypeOptions,
    onSubmit,
    onCancel,
    loading = false,
    disabled = false,
    isRegeneration = false,
    className,
    apiConfig,
    jobStatus,
    onRetry,
    onDismiss,
}) => {
    // EAA-2031: every label is translated (vi/en); none of this chrome is hard-coded English.
    const pt = usePromptTranslations();
    const [formState, setFormState] = React.useState<MatchingFormState>(DEFAULT_FORM_STATE);
    const touchedFieldsRef = React.useRef<Set<keyof MatchingFormState>>(new Set());

    const isDisabled = disabled || loading;
    const hasPrompt = formState.description.trim().length > 0;
    const hasDocuments = formState.documents.length > 0;
    const isFailed = jobStatus?.status === 'failed';
    const canSubmit = (hasPrompt || hasDocuments) && !isDisabled;
    const showRetry = isFailed && !!onRetry;

    const aiContentType = React.useMemo(() => {
        const list = allQuestionTypeOptions ?? questionTypeOptions ?? [];
        return find(list, (o) => o.slug === questionType) ?? null;
    }, [allQuestionTypeOptions, questionTypeOptions, questionType]);

    // EAA-2031: localized catalog label instead of the raw slug when no content-type row is passed.
    const typeDisplayName = getFormTypeDisplayName(questionType, aiContentType?.name, getPromptLocale());
    const typeDescription = aiContentType?.description;

    const presetButtons = React.useMemo(
        () => QUESTION_TYPE_PRESETS[questionType] ?? [],
        [questionType]
    );

    const updateField = React.useCallback(
        <K extends keyof MatchingFormState>(key: K, value: MatchingFormState[K]) => {
            touchedFieldsRef.current.add(key);
            setFormState((prev) => ({ ...prev, [key]: value }));
        },
        []
    );

    // WHY: Agent MatchingQuestionTypeConfig — num_left 2..10, num_right 2..12,
    // and num_right >= num_left. Clamp so FE never submits values Agent rejects.
    const handleNumLeftChange = React.useCallback((value: number) => {
        touchedFieldsRef.current.add('num_left');
        touchedFieldsRef.current.add('num_right');
        const num_left = clampNumLeft(value);
        setFormState((prev) => ({
            ...prev,
            num_left,
            num_right: clampNumRight(prev.num_right, num_left),
        }));
    }, []);

    const handleSubmit = React.useCallback(() => {
        if (!canSubmit && !showRetry) return;
        if (showRetry && onRetry) {
            onRetry();
            return;
        }
        const prompt =
            formState.description.trim() || formState.documents.map((d) => d.name).join(', ');
        const docFields = buildDocumentSubmitFields(formState.documents);
        onSubmit({
            prompt,
            metadata: {
                source_type: formState.sourceType,
                ...docFields.metadata,
            },
            sourceDocument: docFields.sourceDocument,
            // WHY: Agent matching settings are num_left / num_right — never Hub
            // alias num_pairs (GEN_QUESTION_CONTRACT_SSOT §3.3).
            option: {
                media: formState.media,
                num_left: formState.num_left,
                num_right: formState.num_right,
            },
        });
    }, [canSubmit, showRetry, onRetry, formState, onSubmit]);

    const handleDescriptionChange = React.useCallback(
        (value: string) => {
            updateField('description', value);
        },
        [updateField]
    );

    const handleSourceTypeChange = React.useCallback(
        (type: string) => {
            updateField('sourceType', type as MatchingFormState['sourceType']);
        },
        [updateField]
    );

    const handleDocumentsChange = React.useCallback((docs: UploadedDocument[]) => {
        touchedFieldsRef.current.add('documents');
        setFormState((prev) => ({ ...prev, documents: docs }));
    }, []);

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && (canSubmit || showRetry)) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [canSubmit, showRetry, handleSubmit]
    );

    React.useEffect(() => {
        const template = find(QUICK_CREATE_TEMPLATES, (t) => t.slug === questionType);
        if (!template) return;
        setFormState((prev) => {
            const next = { ...prev };
            const touched = touchedFieldsRef.current;
            for (const [key, val] of Object.entries(template.defaults)) {
                const k = key as keyof MatchingFormState;
                if (!touched.has(k)) {
                    (next as Record<string, unknown>)[k] = val;
                }
            }
            return next;
        });
    }, [questionType]);

    return (
        <div className={cn('matching-form w-full min-h-0', className)}>
            <div className="matching-form-panel overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow duration-200 hover:shadow-md">
                {(typeDisplayName || typeDescription) && (
                    <div className="matching-form-type-header border-b border-border/70 bg-muted/25 px-5 py-4 sm:px-6 sm:py-5">
                        <div className="flex gap-3 sm:gap-4">
                            <div
                                className="matching-form-type-icon mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15"
                                aria-hidden
                            >
                                <Sparkles className="h-4 w-4" strokeWidth={2} />
                            </div>
                            <div className="min-w-0 flex-1 space-y-1.5">
                                {typeDisplayName ? (
                                    <h2 className="matching-form-type-name text-base font-semibold leading-snug tracking-tight text-foreground sm:text-lg">
                                        {typeDisplayName}
                                    </h2>
                                ) : null}
                                {typeDescription ? (
                                    <p className="matching-form-type-description text-sm leading-relaxed text-muted-foreground sm:max-w-3xl">
                                        {typeDescription}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}

                <div className="matching-form-section-question border-b border-border/60 sm:px-5">
                    <Card className="matching-form-setup border-0 bg-muted/15 shadow-none py-0">
                        <CardContent className="flex flex-col gap-4 p-4 sm:p-5">
                            <div className="flex items-center justify-between">
                                <h3 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground">
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                        <Sparkles className="h-4 w-4" />
                                    </span>
                                    {pt.questionSetup}
                                </h3>
                            </div>

                            <div className="flex min-w-0 flex-col gap-2.5">
                                <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                                    {pt.media}
                                </Label>
                                <RadioGroup
                                    value={formState.media}
                                    onValueChange={(v) =>
                                        updateField('media', v as MatchingMediaType)
                                    }
                                    disabled={isDisabled}
                                    className="flex w-full flex-wrap gap-2"
                                >
                                    {MEDIA_OPTIONS.map((opt) => (
                                        <label
                                            key={opt.value}
                                            className={cn(
                                                'matching-form-media-option flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors duration-200',
                                                formState.media === opt.value
                                                    ? 'border-primary bg-primary/10 text-primary shadow-sm'
                                                    : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
                                                isDisabled && 'cursor-not-allowed opacity-50'
                                            )}
                                        >
                                            <RadioGroupItem
                                                value={opt.value}
                                                className="h-4 w-4 shrink-0 border-2"
                                            />
                                            {mediaOptionLabel(opt.value, pt)}
                                        </label>
                                    ))}
                                </RadioGroup>
                            </div>

                            <div className="matching-form-counts-grid">
                                <div className="flex min-w-0 flex-col gap-2">
                                    <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                                        {pt.leftItems} <span className="text-destructive">*</span>
                                    </Label>
                                    <Input
                                        type="number"
                                        min={MIN_NUM_LEFT}
                                        max={MAX_NUM_LEFT}
                                        value={formState.num_left}
                                        onChange={(e) =>
                                            handleNumLeftChange(
                                                parseInt(e.target.value, 10) || MIN_NUM_LEFT
                                            )
                                        }
                                        disabled={isDisabled}
                                        className="h-10 rounded-md"
                                    />
                                </div>
                                <div className="flex min-w-0 flex-col gap-2">
                                    <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                                        {pt.rightItems} <span className="text-destructive">*</span>
                                    </Label>
                                    <Input
                                        type="number"
                                        min={Math.max(MIN_NUM_RIGHT, formState.num_left)}
                                        max={MAX_NUM_RIGHT}
                                        value={formState.num_right}
                                        onChange={(e) =>
                                            updateField(
                                                'num_right',
                                                clampNumRight(
                                                    parseInt(e.target.value, 10) ||
                                                        formState.num_left,
                                                    formState.num_left
                                                )
                                            )
                                        }
                                        disabled={isDisabled}
                                        className="h-10 rounded-md"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="matching-form-section-description sm:px-5">
                    <PromptDescriptionInput
                        value={formState.description}
                        onChange={handleDescriptionChange}
                        onKeyDown={handleKeyDown}
                        activePresetKey={formState.sourceType}
                        onActivePresetKeyChange={handleSourceTypeChange}
                        presetButtons={presetButtons}
                        documents={formState.documents}
                        onDocumentsChange={handleDocumentsChange}
                        apiConfig={apiConfig}
                        disabled={isDisabled}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        className="matching-form-description rounded-none border-0 bg-transparent shadow-none ring-0"
                    />
                </div>

                <div className="matching-form-actions flex flex-col gap-3 border-t border-border/80 bg-muted/15 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5">
                    <div className="matching-form-footer-status flex min-w-0 flex-1 items-center justify-start">
                        <JobDetailsBar
                            jobStatus={jobStatus ?? null}
                            onDismiss={onDismiss}
                            className="matching-form-job-details"
                        />
                    </div>
                    <div className="matching-form-footer-actions flex shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
                        {onCancel && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={onCancel}
                                disabled={isDisabled}
                                className="min-h-11 min-w-[88px] px-5 transition-colors duration-200"
                            >
                                {pt.cancel}
                            </Button>
                        )}
                        <Button
                            type="button"
                            size="sm"
                            onClick={handleSubmit}
                            disabled={!canSubmit && !showRetry}
                            className="matching-form-submit min-h-11 gap-2 px-6 font-medium shadow-sm transition-colors duration-200 hover:opacity-[0.97] disabled:opacity-60"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : showRetry ? (
                                <RefreshCw className="h-4 w-4" />
                            ) : isRegeneration ? (
                                <RefreshCw className="h-4 w-4" />
                            ) : (
                                <Sparkles className="h-4 w-4" />
                            )}
                            {loading
                                ? pt.creating
                                : showRetry
                                  ? pt.retry
                                  : isRegeneration
                                    ? pt.regenerate
                                    : pt.createQuestions}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MatchingForm;
